import React from "react";
import { Check } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { Button } from "@/components/ui/button";
import { hasSection, scrollToSection } from "@/storefront/scroll";
import { trackInteraction } from "@/storefront/trackInteraction";
import { rupees } from "@/lib/format";
import { telHref } from "@/lib/phone";

//: A plan's billing interval in months, parsed from its `unit` string — ONLY the shapes
//: `directory/service.py`'s `_MEMBERSHIP_UNIT` actually emits, plus the free-text fallback it
//: falls back to itself (`/{n} months`). Anything else (an `offering_plans` unit like "/kW" or
//: "one-time") is not a billing interval and returns `null` — never guessed.
function intervalMonths(unit) {
  if (unit === "/mo") return 1;
  if (unit === "/quarter") return 3;
  if (unit === "/6 months") return 6;
  if (unit === "/year") return 12;
  const m = /^\/(\d+) months$/.exec(unit || "");
  return m ? Number(m[1]) : null;
}

/** R4-6: the billing interval ("/quarter", "/year") through i18n. Free-text units an owner typed
 * ("/kW", "one-time") are not ours to translate and pass through unchanged. */
function unitText(unit, t) {
  if (!unit) return "";
  const keys = { "/mo": "mo", "/quarter": "quarter", "/year": "year", "/6 months": "sixMonths" };
  if (keys[unit]) return t(`storefront.plans.units.${keys[unit]}`);
  const m = /^\/(\d+) months$/.exec(unit);
  return m ? t("storefront.plans.units.months", { count: m[1] }) : unit;
}

/** D4 — a "Save ₹X/mo" badge, ONLY from real data: derived from the OTHER plans on the SAME
 * business, never invented. Compares this plan's per-month price against the cheapest plan whose
 * interval this business also offers (usually the monthly tier) — if a longer commitment is
 * actually cheaper per month, the badge shows the real difference; otherwise there is no badge.
 * Needs at least 2 plans with a parseable interval and a set price to mean anything. */
function savingsBadge(plan, allPlans, t) {
  const months = intervalMonths(plan.unit);
  if (!months || months <= 1 || plan.price_minor_units == null) return null;
  const comparable = allPlans.filter((p) => {
    const m = intervalMonths(p.unit);
    return m && p.price_minor_units != null && p.id !== plan.id;
  });
  if (comparable.length < 1) return null;
  const baseline = comparable.reduce((min, p) => {
    const perMonth = p.price_minor_units / intervalMonths(p.unit);
    return perMonth < min ? perMonth : min;
  }, Infinity);
  const thisPerMonth = plan.price_minor_units / months;
  if (thisPerMonth >= baseline) return null;
  const totalSavings = Math.round((baseline - thisPerMonth) * months);
  if (totalSavings <= 0) return null;
  return t("storefront.plans.saveBadge", { amount: rupees({ minor_units: totalSavings }) });
}

/** A plan card's price, from the minor-units figure the wire shape already carries — never the
 * server's raw `price_display` string (`str(Decimal)`, e.g. "600.00", "280000.00"), which skips
 * the Indian grouping and the "no needless decimals" rule every other money figure on the site
 * follows. See QA/uat-2026-09-24/04_fix_log.md, Storefront S1, A2. */
function planPriceText(plan, t) {
  if (plan.price_minor_units == null) return t("storefront.plans.getQuote");
  return rupees({ minor_units: plan.price_minor_units, currency: plan.price_currency });
}

/** Where a plan card's CTA sends the visitor, degrading through what the business actually has —
 * never a dead click.
 *
 * F4/L2-006: `subscribe` no longer silently falls through as `enquire` — there is still no
 * checkout/subscription flow to build (none is wanted; a membership sign-up is a real business
 * relationship, not a card payment), but it is now its own distinct "Join" flow into the
 * enquiry capture, prefilled with which plan the visitor wants — same degrade-to-`call` pattern
 * as every other CTA when the business has no `enquiry` section to scroll to. */
function resolvePlanCta(cta, capabilities) {
  if (cta === "call") return { type: "call" };
  if (cta === "book_visit") {
    if (capabilities.includes("service_visit")) return { type: "scroll", key: "service_visit" };
    if (capabilities.includes("enquiry")) return { type: "scroll", key: "enquiry" };
    return { type: "call" };
  }
  if (cta === "subscribe") {
    if (capabilities.includes("enquiry")) return { type: "join" };
    return { type: "call" };
  }
  if (capabilities.includes("enquiry")) return { type: "scroll", key: "enquiry" };
  return { type: "call" };
}

/** `business.plans[]` as a pricing card grid. Capability-agnostic: it shows whenever a business
 * has any plan card, regardless of what `business.capabilities` says (ARCHITECTURE.md §5) — a
 * solar installer's kW tiers and a gym's membership tiers render through the exact same
 * component, because PR2's backend already normalized both sources into one `PlanCard` shape.
 * Rendered by `sectionsFor`'s `plans` pseudo-block (storefront/registry.js); returns null itself
 * too, so it degrades safely if ever mounted directly with no plans. */
export function PlansBlock({ business }) {
  const { t, lang, setEnquiryPrefill } = useApp();
  const plans = business.plans || [];
  if (plans.length === 0) return null;
  const capabilities = business.capabilities || [];

  // C3 — a priced services list (salon "Services & prices", electrician "What we fix"): when
  // EVERY card on this business books a visit rather than sells a tier, the same `offering_plans`
  // data renders as a flat priced list instead of the tier-card grid, per
  // `07_storefront_design_gap.md`'s C3 ("reusing offering_plans... rendered as a flat list").
  // One registry entry, one data source — never both shapes on the same page.
  const isServiceList = plans.every((p) => p.cta === "book_visit");

  if (isServiceList) {
    return (
      <div className="space-y-1" data-testid="storefront-plans" id="storefront-services">
        <h2 className="font-heading text-xl font-semibold">{t("storefront.plans.servicesTitle")}</h2>
        <div className="divide-y divide-border">
          {plans.map((plan) => {
            const resolved = resolvePlanCta(plan.cta, capabilities);
            const planName = lang === "hi" ? plan.name_hi || plan.name : plan.name;
            const priceText = planPriceText(plan, t);
            const activate = () => {
              if (resolved.type === "call") trackInteraction(business.id, "call");
              else if (hasSection(resolved.key)) scrollToSection(resolved.key);
              else {
                // B1: the target section is not on the page (gated off) - dial, never a dead scroll.
                const href = telHref(business.phone);
                if (!href) return;
                trackInteraction(business.id, "call");
                window.location.href = href;
              }
            };
            return (
              <div
                key={plan.id}
                data-testid={`storefront-plan-${plan.id}`}
                className="flex flex-wrap items-center justify-between gap-3 py-3.5"
              >
                <div>
                  <p className="font-medium">{planName}</p>
                  {plan.spec_rows.length > 0 && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {plan.spec_rows.map((r) => r.value || r.label).join(" · ")}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3.5">
                  <span
                    className="tabular-nums text-base font-bold"
                    data-testid={`storefront-plan-price-${plan.id}`}
                  >
                    {priceText}
                  </span>
                  {resolved.type === "call" ? (
                    <Button
                      asChild
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      data-testid={`storefront-plan-cta-${plan.id}`}
                      onClick={activate}
                    >
                      <a href={telHref(business.phone) ?? undefined}>{t("storefront.plans.ctaBookVisit")}</a>
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      data-testid={`storefront-plan-cta-${plan.id}`}
                      onClick={activate}
                    >
                      {t("storefront.plans.ctaBookVisit")}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="storefront-plans">
      <h2 className="font-heading text-xl font-semibold">{t("storefront.plans.title")}</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {plans.map((plan) => {
          const resolved = resolvePlanCta(plan.cta, capabilities);
          const planName = lang === "hi" ? plan.name_hi || plan.name : plan.name;
          const priceText = planPriceText(plan, t);
          const label =
            resolved.type === "join"
              ? t("storefront.plans.ctaJoin")
              : resolved.type === "call"
                ? plan.cta === "subscribe"
                  ? t("storefront.plans.ctaJoin")
                  : t("storefront.plans.ctaCall")
                : resolved.key === "service_visit"
                  ? t("storefront.plans.ctaBookVisit")
                  : t("storefront.plans.ctaEnquire");
          const activate = () => {
            if (resolved.type === "join") {
              setEnquiryPrefill({
                message: t("storefront.plans.joinPrefill", {
                  plan: planName,
                  price: priceText,
                  interval: unitText(plan.unit, t).replace(/^\//, ""),
                }),
              });
              scrollToSection("enquiry");
              return;
            }
            if (resolved.type === "call") trackInteraction(business.id, "call");
            else if (hasSection(resolved.key)) scrollToSection(resolved.key);
            else {
              const href = telHref(business.phone);
              if (!href) return;
              trackInteraction(business.id, "call");
              window.location.href = href;
            }
          };

          const badge = savingsBadge(plan, plans, t);

          return (
            <div
              key={plan.id}
              data-testid={`storefront-plan-${plan.id}`}
              className={`flex flex-col overflow-hidden rounded-2xl border ${
                plan.featured
                  ? "border-2 border-[hsl(var(--primary))] shadow-[0_18px_40px_-20px_hsl(var(--primary)/0.5)]"
                  : "border-border"
              }`}
            >
              {plan.featured && (
                <div className="bg-[hsl(var(--primary))] py-1.5 text-center text-[11px] font-bold uppercase tracking-widest text-white">
                  {t("storefront.plans.featured")}
                </div>
              )}
              <div className="flex flex-1 flex-col gap-3 p-5">
                <p className="font-heading text-base font-semibold">{planName}</p>
                {/* Formatted from `price_minor_units` via the shared rupees() formatter — a null
                    price is a "Quote" card, never ₹0 (see planPriceText above). */}
                <p className="tabular-nums text-lg font-semibold" data-testid={`storefront-plan-price-${plan.id}`}>
                  {plan.price_minor_units != null ? `${priceText}${plan.unit ? ` ${unitText(plan.unit, t)}` : ""}` : priceText}
                </p>
                {badge && (
                  <span
                    className="inline-block w-fit rounded-full bg-[hsl(var(--accent))]/15 px-2.5 py-0.5 text-xs font-semibold text-[hsl(var(--accent-ink))]"
                    data-testid={`storefront-plan-savings-${plan.id}`}
                  >
                    {badge}
                  </span>
                )}
                {(plan.features || []).length > 0 && (
                  <ul className="space-y-1.5 text-sm">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Check size={14} className="shrink-0 text-[hsl(var(--primary))]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                )}
                {plan.spec_rows.length > 0 && (
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    {plan.spec_rows.map((row, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span>{row.label}</span>
                        {row.value && <span className="font-medium text-foreground">{row.value}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-auto pt-1">
                  {resolved.type === "call" ? (
                    <Button asChild className="w-full rounded-full" data-testid={`storefront-plan-cta-${plan.id}`} onClick={activate}>
                      <a href={telHref(business.phone) ?? undefined}>{label}</a>
                    </Button>
                  ) : (
                    <Button className="w-full rounded-full" data-testid={`storefront-plan-cta-${plan.id}`} onClick={activate}>
                      {label}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
